// lib/auth-client.ts

import { adminClient, magicLinkClient } from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react' // make sure to import from better-auth/react

function resolveAuthClientBaseURL(): string | undefined {
  const configuredUrl = process.env.NEXT_PUBLIC_APP_URL?.trim()
  if (configuredUrl) return configuredUrl

  if (typeof window !== 'undefined') {
    return window.location.origin
  }

  // Server-side module evaluation (this file has no 'use client' boundary of
  // its own, so it also runs on the server while Next.js SSRs the client
  // components that import it): there's no window to read an origin from, so
  // resolve against the same server-only env vars the auth server config
  // (src/lib/auth.ts) already trusts, instead of silently depending on
  // better-auth's own internal fallback chain.
  const serverConfiguredUrl = process.env.BETTER_AUTH_URL?.trim()
  if (serverConfiguredUrl) return serverConfiguredUrl

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`
  }

  return undefined
}

const authClientBaseURL = resolveAuthClientBaseURL()

function rateLimitUserMessage(retryAfterHeader: string | null): string {
  const seconds = Number(retryAfterHeader)
  if (Number.isFinite(seconds) && seconds > 0) {
    const rounded = Math.ceil(seconds)
    return `Too many attempts. Try again in ${rounded} second${rounded === 1 ? '' : 's'}.`
  }
  return 'Too many attempts. Please try again shortly.'
}

export const authClient = createAuthClient({
  // Prefer NEXT_PUBLIC_APP_URL when inlined at build time. In the browser,
  // fall back to the current origin so preview/local URLs work without a
  // hardcoded localhost default. On the server, fall back to BETTER_AUTH_URL
  // / VERCEL_URL so baseURL resolves explicitly instead of relying on
  // better-auth's internal env fallback.
  ...(authClientBaseURL ? { baseURL: authClientBaseURL } : {}),
  fetchOptions: {
    onError(context) {
      if (context.response?.status !== 429) return
      const message = rateLimitUserMessage(
        context.response.headers.get('X-Retry-After'),
      )
      if (context.error && typeof context.error === 'object') {
        context.error.message = message
      }
    },
  },
  plugins: [adminClient(), magicLinkClient()],
})

export const DEFAULT_AUTH_CALLBACK_URL = '/assistant'

function navigateForOAuth(redirectUrl: string): void {
  // OAuth requires full document navigation (302 chain to the provider).
  const link = document.createElement('a')
  link.href = new URL(redirectUrl, window.location.origin).href
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

const signInSocial = async (
  provider: 'github' | 'google' | 'microsoft',
  callbackURL: string,
) => {
  // disableRedirect returns JSON { url } or { error } instead of navigating
  // immediately, so callers can surface failures and reset pending UI state.
  const { data, error } = await authClient.signIn.social({
    provider,
    callbackURL,
    disableRedirect: true,
  })

  if (error) {
    throw new Error(
      error.message ??
        `Sign in with ${provider} failed (${error.status} ${error.statusText})`,
    )
  }

  const redirectUrl = data?.url
  if (!redirectUrl) {
    throw new Error(`Sign in with ${provider} did not return a redirect URL.`)
  }

  navigateForOAuth(redirectUrl)
}

export const signInGithub = async (callbackURL = DEFAULT_AUTH_CALLBACK_URL) => {
  await signInSocial('github', callbackURL)
}

export const signInGoogle = async (callbackURL = DEFAULT_AUTH_CALLBACK_URL) => {
  await signInSocial('google', callbackURL)
}

export const signInMicrosoft = async (
  callbackURL = DEFAULT_AUTH_CALLBACK_URL,
) => {
  await signInSocial('microsoft', callbackURL)
}

export const signInEmail = async (
  email: string,
  password: string,
  callbackURL = DEFAULT_AUTH_CALLBACK_URL,
) => {
  return authClient.signIn.email({
    email,
    password,
    callbackURL,
  })
}
